import AdjustIcon from '@mui/icons-material/Adjust';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button, Grid, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';

import { CameraInfo, RdConnectInstance, RdConnectInstancePingResponse } from '@apiContract';
import { MotionContainer, varBounce } from '@components/Animate';
import { AutomatedCameraLocalStorageKeys, CapturePiece, MapCapturePiece } from '@components/Capture/types';
import useLocalStorage from '@hooks/useLocalStorage';
import { usePingRequest } from '@hooks/usePingRequest';
import { m } from 'framer-motion';
import orderBy from 'lodash/fp/orderBy';

import { RdConnectPingListParams, getRdConnectInstancePing } from '@src/api/automatedCapture';

import CameraStatus from './CameraStatus';
import { Delete } from './Delete';
import FilesList from './FilesList';
import { sxContainer, sxHeaderItem, sxImageContainer, sxMain, sxSortRow } from './styles';
import { RDInstanceRequestStatus } from 'src/types/shared';

type Props = {
  files: MapCapturePiece;
  onStartAutomatedCapture: VoidFunction;
  onStopAutomatedCapture: VoidFunction;
  sessionId: string;
  userDevice: RdConnectInstance;
  onDelete: VoidFunction;
  isCaptureInProgress: boolean;
  setUserDevice: Dispatch<SetStateAction<RdConnectInstance>>;
  onChooseCamera: VoidFunction;
  userCamera: CameraInfo;
  disableCapture?: boolean;
};

enum SortOrder {
  DESC = 'desc',
  ASC = 'asc',
}

const AutomatedCapture: FC<Props> = ({
  files,
  onStartAutomatedCapture,
  onStopAutomatedCapture,
  sessionId,
  onDelete,
  userDevice,
  isCaptureInProgress,
  setUserDevice,
  onChooseCamera,
  disableCapture = false,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortOrder, setSortOrder] = useLocalStorage<SortOrder>(
    AutomatedCameraLocalStorageKeys.DraftPiecesSortOrder,
    SortOrder.ASC,
  );
  const userDeviceId = userDevice?.id as number;
  const { status, execute } = usePingRequest<RdConnectInstancePingResponse, RdConnectPingListParams>(
    getRdConnectInstancePing,
    {
      maxRetries: 3,
      retryDelay: 2000,
      initialParams: { rdConnectInstanceId: userDeviceId },
    },
  );

  const filesQuantity = Object.keys(files).length;
  const success = status === RDInstanceRequestStatus.SUCCESS;
  const loading = status === RDInstanceRequestStatus.LOADING || status === RDInstanceRequestStatus.RETRYING;

  const handleCapture = isCaptureInProgress ? onStopAutomatedCapture : onStartAutomatedCapture;
  const onDeleteFinish = () => {
    onDelete();
    setShowDeleteModal(false);
  };

  const sortedFiles = useMemo(() => {
    const fileValues = Object.values(files);
    const sorter = orderBy([(file: CapturePiece) => file?.piece?.pieceLocation?.orderNo])([sortOrder]);

    return sorter(fileValues);
  }, [files, sortOrder]);

  return (
    <Box sx={sxMain}>
      <Grid container sx={sxContainer}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={sxHeaderItem}>
            <CameraStatus
              success={success}
              loading={loading}
              makeRequest={execute}
              rdConnectInstanceId={userDeviceId}
              setUserDevice={setUserDevice}
              onChooseCamera={onChooseCamera}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={sxHeaderItem}>
            <Button
              size="large"
              variant="soft"
              color={isCaptureInProgress ? 'error' : 'inherit'}
              startIcon={isCaptureInProgress ? <StopIcon /> : <AdjustIcon />}
              onClick={handleCapture}
              disabled={!success || disableCapture}
            >
              {isCaptureInProgress ? 'Stop Capturing' : 'Start Capturing'}
            </Button>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={sxHeaderItem}>
            <Typography variant="h4">{filesQuantity}</Typography>
            <Typography variant="body2" color="text.secondary">
              images captured
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={sxSortRow}>
        <Box>
          <Typography variant="body2" fontWeight="bold" display="inline">
            Sort by:
          </Typography>
          <Select
            variant="standard"
            value={sortOrder}
            sx={{ ml: 1, width: 80 }}
            onChange={({ target }: SelectChangeEvent<SortOrder>) => {
              setSortOrder(target.value as SortOrder);
            }}
          >
            <MenuItem value={SortOrder.DESC}>Newest</MenuItem>
            <MenuItem value={SortOrder.ASC}>Oldest</MenuItem>
          </Select>
        </Box>

        <Button
          size="small"
          variant="text"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          disabled={filesQuantity === 0 || isCaptureInProgress}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete All
        </Button>
      </Box>

      {filesQuantity > 0 ? (
        <FilesList items={sortedFiles} />
      ) : (
        <Box sx={sxImageContainer}>
          <MotionContainer>
            <m.div variants={varBounce('in')}>
              <Box sx={{ my: 10, textAlign: 'center' }}>
                <img src="/assets/illustrations/folder-empty.svg" alt="No Images" />
                <Typography variant="h6" color="text.disabled">
                  No images to display
                </Typography>
              </Box>
            </m.div>
          </MotionContainer>
        </Box>
      )}
      <Delete
        sessionId={sessionId}
        actionStarted={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onActionFinish={onDeleteFinish}
      />
    </Box>
  );
};

export default AutomatedCapture;
